import { createContext, useContext, useState, useRef, ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { launchConfigService } from '@/services/launch-config.service';
import { valuationService } from '@/services/valuation.service';
import { toast } from 'react-hot-toast';

type RefreshTask =
  | 'downloadFixedRates'
  | 'downloadForwardCurves'
  | 'refreshMaterializedViews'
  | null;

type ActiveRefreshTask = Exclude<RefreshTask, null>;

export const REFRESH_TASK_MESSAGES: Record<ActiveRefreshTask, string> = {
  downloadFixedRates:
    'Download and persist swap rate curves (fixed rates) for multiple indices from BlueGamma API.',
  downloadForwardCurves:
    'Download and persist forward curves for multiple indices from BlueGamma API.',
  refreshMaterializedViews:
    'Recomputing and refreshing valuation datasets for all loans.',
};

interface GlobalRefreshContextType {
  refreshing: boolean;
  longRefresh?: boolean;
  currentTask: ActiveRefreshTask[];
  handleRefresh: (valuationDate: string) => void;
}

const GlobalRefreshContext = createContext<
  GlobalRefreshContextType | undefined
>(undefined);

export const GlobalRefreshProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [longRefresh, setLongRefresh] = useState(false);
  const [currentTask, setCurrentTask] = useState<ActiveRefreshTask[]>([]);

  // Store flag and timeout in refs to work correctly inside callbacks
  const longRefreshRef = useRef(false);
  const longRefreshTimeoutRef = useRef<number | null>(null);

  const setLongRefreshState = (value: boolean) => {
    longRefreshRef.current = value;
    setLongRefresh(value);
  };

  const appendTask = (task: ActiveRefreshTask) => {
    setCurrentTask((prevTask) => [...prevTask, task]);
  };

  const refreshViewsMutation = useMutation({
    mutationFn: async ({ valuationDate }: { valuationDate: string }) => {
      appendTask('downloadFixedRates');
      await valuationService.downloadAddFixedRates({ valuationDate });
      appendTask('downloadForwardCurves');
      await valuationService.downloadAddForwardCurves({ valuationDate });
      appendTask('refreshMaterializedViews');
      return launchConfigService.refreshMaterializedViews();
    },
    retry: 0,
    onSuccess: async (result) => {
      // Clear long-refresh timeout if it was running
      if (longRefreshTimeoutRef.current !== null) {
        window.clearTimeout(longRefreshTimeoutRef.current);
        longRefreshTimeoutRef.current = null;
      }

      toast.success(`Refresh completed in ${result.duration_seconds}s`);
      setLongRefreshState(false);
      // Invalidate all queries to refetch with fresh data
      await queryClient.invalidateQueries();
      setRefreshing(false);
      setCurrentTask([]);
    },
    onError: (error) => {
      const axiosError = error as {
        response?: { status?: number };
        code?: string;
        config?: { url?: string };
      };
      const status = axiosError?.response?.status;
      const code = axiosError?.code;
      const url = axiosError?.config?.url;

      console.log('[GlobalRefresh] onError triggered', {
        status,
        code,
        url,
      });

      const isRefreshEndpoint =
        typeof url === 'string' &&
        url.includes('/api/v1/valuation/refresh-materialized-views');

      // Backend timeout (504) or network timeout specifically for the refresh endpoint.
      // In this case we assume backend still processes in background and
      // keep showing the spinner for additional 220 seconds.
      if (
        (status === 504 || code === 'ECONNABORTED' || code === 'ERR_NETWORK') &&
        isRefreshEndpoint
      ) {
        console.log(
          '[GlobalRefresh] Long refresh path triggered',
          `status=${status || 'n/a'}`,
          `code=${code || 'n/a'}`,
          `url=${url}`
        );

        // Mark as "long" refresh
        setLongRefreshState(true);
        setRefreshing(true);

        // Clear any existing long-refresh timer
        if (longRefreshTimeoutRef.current !== null) {
          window.clearTimeout(longRefreshTimeoutRef.current);
        }

        // 220 seconds = 220_000 ms
        longRefreshTimeoutRef.current = window.setTimeout(() => {
          setLongRefreshState(false);
          setRefreshing(false);
          setCurrentTask([]);
          longRefreshTimeoutRef.current = null;
        }, 220_000);

        // Do not show any toast error for this case
        return;
      }

      // For any other case we also do not show toast,
      // only log to console to avoid scaring the user.
      console.error('Error refreshing materialized views (non-long case):', {
        status,
        code,
        url,
        error,
      });

      if (!longRefreshRef.current) {
        setCurrentTask([]);
      }
    },
    onSettled: () => {
      // If NOT a long refresh – perform normal completion
      if (!longRefreshRef.current) {
        setRefreshing(false);
      }
    },
  });

  const handleRefresh = (valuationDate: string) => {
    setCurrentTask([]);
    setLongRefresh(false);
    setRefreshing(true);
    refreshViewsMutation.mutate({ valuationDate });
  };

  return (
    <GlobalRefreshContext.Provider
      value={{
        refreshing,
        longRefresh,
        currentTask,
        handleRefresh,
      }}
    >
      {children}
    </GlobalRefreshContext.Provider>
  );
};

export const useGlobalRefresh = () => {
  const context = useContext(GlobalRefreshContext);
  if (context === undefined) {
    throw new Error(
      'useGlobalRefresh must be used within GlobalRefreshProvider'
    );
  }
  return context;
};
